import httpRequest from '../request/indexBase'
import { local, getEditToken } from '@/utils/storage'
import { menuList, newMenuList } from '../mockData/menuData'
import { getVersionType } from '@/utils/checkStatus'
import { isDemoUser } from '@/utils/menuDisplayConfig'

/**
 * 过滤灰度菜单并处理菜单互斥。
 *
 * 1. 灰度：菜单项未声明 demoOnly 时对所有人可见；声明了则由后端下发的显示配置决定
 *    （见 @/utils/menuDisplayConfig）。
 * 2. 互斥：保留下来的菜单项可通过 replaceMenuKeys 声明它要顶替的旧菜单，
 *    被顶替的菜单（按 permissionsKey 匹配）随之隐藏，避免侧栏出现两个同名入口。
 *
 * 注意：这只是界面可见性，不是访问控制；最终授权必须由服务端在接口侧判定。
 *
 * @param {Array} menus 菜单数组
 * @returns {Promise<Array>} 过滤后的菜单数组
 */
async function filterDemoMenus(menus) {
  // 判定要问一次接口，整批菜单只判一次；结果按 TOKEN 缓存，不会每次加载菜单都发请求
  const hasDemoAccess = menus.some((item) => item.demoOnly) ? await isDemoUser() : false
  const visibleMenus = menus.filter((item) => !item.demoOnly || hasDemoAccess)
  const replacedKeys = new Set(
    visibleMenus.flatMap((item) =>
      Array.isArray(item.replaceMenuKeys) ? item.replaceMenuKeys : []
    )
  )
  if (!replacedKeys.size) return visibleMenus
  return visibleMenus.filter((item) => !replacedKeys.has(item.permissionsKey))
}

// 文件上传
export function uploadFile(data, code) {
  return httpRequest.post({
    url: `/repository/files-record/fileUpload`,
    data,
    specialToken: code ? getEditToken() : local.get('TOKEN')
  })
}

/**
 * 外部图片 URL 转存 OBS：后端下载这张图并上传到华为云 OBS，返回 OBS 访问链接。
 * 支持带签名参数的预签名链接（这类链接会过期，转存后才能长期使用）。
 *
 * @param {Object} data
 * @param {string} data.imageUrl 待转存的图片 URL
 * @returns {Promise<{data: {imageUrl: string}}>} data.imageUrl 为转存后的 OBS 图片链接
 */
export function uploadImageFromUrl(data) {
  return httpRequest.post({
    url: `/repository/files-record/imageCopy`,
    data
  })
}

// 获取对应权限的菜单
export function getMenuList(params) {
  // SoloSmart2 菜单先使用本地固定数据，后续接 API 时改为 false。
  const useMock = true
  if (useMock) {
    return filterDemoMenus(getVersionType() === 'pro' ? newMenuList : menuList).then((data) => ({
      data
    }))
  } else {
    return httpRequest.get({
      url: `/permission/resource/getMenuListRoleTree`,
      params
    })
  }
}
// 查询用户团队角色
export function getUserTeamRole(params) {
  return httpRequest.get({
    url: `/permission/team-user/getUserTeamRole`,
    params
  })
}

/**
 * 按编码查一棵字典树。平台通用字典，不属于某个业务模块。
 *
 * 当前用处：Story board 按脚本的 subCode 取一套分镜字段，新增镜头时按它建内容
 * （见 storyboard.vue 的 loadShotTemplate）。
 *
 * @param {Object} params
 * @param {string} params.code 字典编码
 * @returns {Promise<{data: Array}>} 字典树，节点可带 children
 */
export function queryDictionaryTree(params) {
  return httpRequest.get({
    url: `/dictionary-item/code/tree`,
    params
  })
}
